import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, LogOut, User as UserIcon, GraduationCap, Briefcase, ArrowLeft, ArrowRight, Menu, X, Loader2, Shapes, Sun, Moon, LayoutDashboard, FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export function Navbar() {
  const { user, role, hasTutorAccess, login, logout, toggleRole, isLoggingIn, openAuth, memberships } = useAuth();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrivingMode, setIsDrivingMode] = useState(() => {
    return localStorage.getItem('lumina_driving_mode') === 'true';
  });
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('lumina_theme') as 'light' | 'dark') || 'dark';
  });

  useEffect(() => {
    const handleThemeChange = (e: any) => {
      setTheme(e.detail);
    };
    window.addEventListener('themeChanged', handleThemeChange);
    return () => window.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: newTheme }));
  };

  // Dispatch custom event when driving mode changes so CoursePlayer can listen
  const toggleDrivingMode = () => {
    const newValue = !isDrivingMode;
    setIsDrivingMode(newValue);
    localStorage.setItem('lumina_driving_mode', String(newValue));
    window.dispatchEvent(new CustomEvent('drivingModeChanged', { detail: newValue }));
  };

  return (
    <>
      <nav className="bg-bg-alt border-b border-border-main sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1 mr-2 border-r border-border-main pr-4 hidden sm:flex">
                <button
                  onClick={() => navigate(-1)}
                  className="p-1.5 text-text-alt hover:text-text-main hover:bg-bg-main/50 rounded-lg transition-colors"
                  title="Go back"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate(1)}
                  className="p-1.5 text-text-alt hover:text-text-main hover:bg-bg-main/50 rounded-lg transition-colors"
                  title="Go forward"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
              <a href="/" className="flex items-center gap-3">
                <div className="shrink-0 shadow-lg p-1 bg-bg-alt rounded-xl border border-border-main">
                  <svg width="32" height="32" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="100" height="100" rx="22" fill="currentColor" className="text-bg-main" />
                    <path d="M25 75 L25 35 L45 20 L60 50 L35 75 Z" fill="currentColor" className="text-text-alt opacity-40" />
                    <path d="M40 75 L70 20 L80 20 L50 75 Z" fill="#00E5FF" />
                    <path d="M55 75 L85 75 L75 55 Z" fill="currentColor" className="text-text-alt opacity-60" />
                    <circle cx="75" cy="68" r="4" fill="#f97316" />
                  </svg>
                </div>
                <span className="text-xl tracking-tight flex items-center gap-1.5">
                  <span className="font-bold text-text-main">Lumina</span>
                  <span className="font-normal text-text-alt">Learn</span>
                </span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden sm:flex items-center gap-4">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-text-alt hover:text-text-main hover:bg-bg-main/50 rounded-full transition-all border border-border-main"
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5 text-amber-400" />
                ) : (
                  <Moon className="h-5 w-5 text-indigo-500" />
                )}
              </button>

              {/* Driving Mode Toggle */}
              <button
                onClick={toggleDrivingMode}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                  isDrivingMode
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.2)]'
                    : 'bg-bg-main text-text-alt border-border-main hover:text-text-main hover:bg-bg-main/80'
                }`}
                title="Hands-Free Driving Mode"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="21.17" y1="8" x2="12" y2="8"/>
                  <line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
                  <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
                </svg>
                Driving Mode
              </button>

              {user && role === 'tutor' && (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-bg-main text-text-alt border border-border-main hover:text-text-main hover:border-indigo-500/50"
                  title="Main Dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Link>
              )}
              {user && hasTutorAccess && (
                <Link
                  to="/source-sets"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-bg-main text-text-alt border border-border-main hover:text-text-main hover:border-indigo-500/50"
                  title="Source Sets"
                >
                  <FileText className="h-4 w-4" />
                  Source Sets
                </Link>
              )}


              {user && memberships.length > 0 && hasTutorAccess && (
                <Link
                  to="/organisation"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors bg-bg-main text-text-alt border border-border-main hover:text-text-main"
                  title="Organisation Dashboard"
                >
                  <Shapes className="h-4 w-4" />
                  Org
                </Link>
              )}

              {user ? (
                <>
                  <div className="flex items-center bg-bg-main rounded-full p-1 border border-border-main">
                    <button
                      onClick={async () => {
                        if (role !== 'learner') {
                          await toggleRole();
                          navigate('/', { replace: true });
                        }
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        role === 'learner'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-text-alt hover:text-text-main'
                      }`}
                    >
                      <GraduationCap className="h-4 w-4" />
                      Learner
                    </button>
                    <button
                      onClick={async () => {
                        if (hasTutorAccess && role !== 'tutor') {
                          await toggleRole();
                          navigate('/dashboard', { replace: true });
                        }
                      }}
                      disabled={!hasTutorAccess}
                      title={!hasTutorAccess ? "Tutor mode is not available for learner accounts." : ""}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        !hasTutorAccess
                          ? 'opacity-40 cursor-not-allowed bg-bg-main text-text-alt'
                          : role === 'tutor'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-text-alt hover:text-text-main'
                      }`}
                    >
                      <Briefcase className="h-4 w-4" />
                      Tutor
                    </button>
                  </div>

                  <div className="flex items-center gap-3 pl-4 border-l border-border-main">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || 'User'} className="h-8 w-8 rounded-full border border-border-main" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-bg-main flex items-center justify-center border border-border-main">
                        <UserIcon className="h-4 w-4 text-text-alt" />
                      </div>
                    )}
                    <button
                      onClick={logout}
                      className="p-2 text-text-alt hover:text-text-main transition-colors rounded-full hover:bg-bg-main"
                      title="Sign Out"
                    >
                      <LogOut className="h-5 w-5" />
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => openAuth('login')}
                  disabled={isLoggingIn}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoggingIn ? 'Signing in...' : 'Sign In'}
                </button>
              )}
            </div>

            {/* Mobile Hamburger Menu */}
            <div className="flex sm:hidden items-center gap-4">
              {/* Theme Toggle (Mobile accessible from top) */}
              <button
                onClick={toggleTheme}
                className="p-2 text-text-alt hover:text-text-main hover:bg-bg-main/50 rounded-full transition-all border border-border-main"
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5 text-amber-400" />
                ) : (
                  <Moon className="h-5 w-5 text-indigo-500" />
                )}
              </button>
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="p-2 text-text-alt hover:text-text-main"
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-[60] flex sm:hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsDrawerOpen(false)}
          />
          <div className="relative flex w-full max-w-xs flex-1 flex-col bg-bg-alt border-r border-border-main pt-5 pb-4">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setIsDrawerOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <X className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>

            <div className="flex shrink-0 items-center px-4 mb-8">
              <a href="/" className="flex items-center gap-3">
                <div className="shrink-0 shadow-lg p-1 bg-bg-main rounded-xl border border-border-main">
                  <svg width="32" height="32" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="100" height="100" rx="22" fill="currentColor" className="text-bg-alt" />
                    <path d="M25 75 L25 35 L45 20 L60 50 L35 75 Z" fill="currentColor" className="text-text-alt opacity-40" />
                    <path d="M40 75 L70 20 L80 20 L50 75 Z" fill="#00E5FF" />
                    <path d="M55 75 L85 75 L75 55 Z" fill="currentColor" className="text-text-alt opacity-60" />
                    <circle cx="75" cy="68" r="4" fill="#f97316" />
                  </svg>
                </div>
                <span className="text-xl tracking-tight flex items-center gap-1.5">
                  <span className="font-bold text-text-main">Lumina</span>
                  <span className="font-normal text-text-alt">Learn</span>
                </span>
              </a>
            </div>

            <div className="mt-5 h-0 flex-1 overflow-y-auto px-4">
              {user ? (
                <div className="flex flex-col gap-6">
                  {/* User Profile */}
                  <div className="flex items-center gap-3">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName || 'User'} className="h-10 w-10 rounded-full border border-border-main" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-bg-main flex items-center justify-center border border-border-main">
                        <UserIcon className="h-5 w-5 text-text-alt" />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-text-main">{user.displayName || 'User'}</span>
                      <span className="text-xs text-text-alt opacity-60">{user.email}</span>
                    </div>
                  </div>

                  {/* Status / Role Badge */}
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-text-alt opacity-60 uppercase tracking-wider">Navigation</span>

                    {role === 'tutor' && (
                      <Link
                        to="/dashboard"
                        onClick={() => setIsDrawerOpen(false)}
                        className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors border bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-500/20"
                      >
                        <LayoutDashboard className="h-5 w-5" />
                        Main Dashboard
                      </Link>
                    )}
                    {hasTutorAccess && (
                      <Link
                        to="/source-sets"
                        onClick={() => setIsDrawerOpen(false)}
                        className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors border bg-bg-main text-text-alt border-border-main hover:text-text-main"
                      >
                        <FileText className="h-5 w-5" />
                        Source Sets
                      </Link>
                    )}


                    <div className="flex items-center bg-bg-main rounded-lg p-1 border border-border-main">
                      <button
                        onClick={() => {
                          if (role !== 'learner') toggleRole();
                          setIsDrawerOpen(false);
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          role === 'learner'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-text-alt hover:text-text-main'
                        }`}
                      >
                        <GraduationCap className="h-4 w-4" />
                        Learner
                      </button>
                      <button
                        onClick={() => {
                          if (hasTutorAccess && role !== 'tutor') toggleRole();
                          setIsDrawerOpen(false);
                        }}
                        disabled={!hasTutorAccess}
                        title={!hasTutorAccess ? "Tutor mode is not available for learner accounts." : ""}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          !hasTutorAccess
                            ? 'opacity-40 cursor-not-allowed bg-bg-main text-text-alt'
                            : role === 'tutor'
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : 'text-text-alt hover:text-text-main'
                        }`}
                      >
                        <Briefcase className="h-4 w-4" />
                        Tutor
                      </button>
                    </div>
                  </div>

                  {/* Driving Mode Toggle (Mobile) */}
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-text-alt opacity-60 uppercase tracking-wider">Features</span>

                    {memberships.length > 0 && hasTutorAccess && (
                      <Link
                        to="/organisation"
                        onClick={() => setIsDrawerOpen(false)}
                        className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors border bg-bg-main text-text-alt border-border-main hover:text-text-main"
                      >
                        <Shapes className="h-5 w-5" />
                        Organisation Dashboard
                      </Link>
                    )}

                    <button
                      onClick={toggleDrivingMode}
                      className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors border ${
                        isDrivingMode
                          ? 'bg-emerald-500/20 text-emerald-500 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.2)]'
                          : 'bg-bg-main text-text-alt border-border-main hover:text-text-main'
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <circle cx="12" cy="12" r="4"/>
                        <line x1="21.17" y1="8" x2="12" y2="8"/>
                        <line x1="3.95" y1="6.06" x2="8.54" y2="14"/>
                        <line x1="10.88" y1="21.94" x2="15.46" y2="14"/>
                      </svg>
                      {isDrivingMode ? 'Driving Mode: ON' : 'Driving Mode: OFF'}
                    </button>
                  </div>

                  {/* Logout */}
                  <div className="mt-auto pt-6 border-t border-border-main">
                    <button
                      onClick={() => {
                        logout();
                        setIsDrawerOpen(false);
                      }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm font-medium text-rose-500 hover:text-rose-600 hover:bg-bg-main rounded-lg transition-colors"
                    >
                      <LogOut className="h-5 w-5" />
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    openAuth('login');
                    setIsDrawerOpen(false);
                  }}
                  disabled={isLoggingIn}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoggingIn ? 'Signing in...' : 'Sign In'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
